/**
 * 图片生成配置节点（卡片极简 + 下方面板架构，对齐 infinite-canvas 布局）：
 *  - 卡片内：模式分段、模型选择、参数摘要按钮（弹层编辑比例/质量/数量）、蒙版（inpaint）、生成按钮
 *  - 下方面板（选中时展开）：上游输入排序、补充提示词（点选/引用/提示词库）、运行链路
 *
 * 关键：模型列表使用 services/api.getModels()（与首页同步），不重复请求。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Wand2, Loader2, BookOpen, ArrowUp, ArrowDown, Paintbrush, Play, ChevronDown, AtSign, MousePointerClick, Maximize2 } from 'lucide-react';
import type { CanvasNode, GenerateImageNodeData, TextNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeParamsPopover } from './NodeParamsPopover';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import {
  aggregateUpstream,
  buildUpstreamSignature,
  collectUpstreamImageAssetGroups,
  waitForUpstreamIdle,
} from '../state/upstream';
import { resolveAtReferences } from '../atReference';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { htmlToPlainText } from '../utils/richTextBox';
import { runImageGeneration } from '../generators/imageGenerator';
import { executeChain } from '../chainExecutor';
import type { GenerationMode, ModelConfig } from '../../../services/api';
import { getCachedImageModels } from '../modelCache';
import { writeNodeParamMemory } from '../utils/nodeParamMemory';
import { PromptLibraryModal } from '../PromptLibraryModal';
import {
  PromptMentionEditor,
  type PromptMentionEditorHandle,
} from './PromptMentionEditor';
import { MaskDialog } from '../../../components/MaskDialog';
import { ImageCreditsPreview } from '../ImageCreditsPreview';
import { TextEditorDialog } from '../components/dialogs/TextEditorDialog';
import toast from '../../../components/Toast';
import { uploadMaskToIndexedDB } from '../imageStorage';
import { useUpstreamModeSync } from '../hooks/useUpstreamModeSync';
import { validateImageReferenceQuota } from '../utils/referenceImageQuota';
import { getPromptLengthError } from '../utils/promptLimit';

interface GenerateImageNodeProps {
  node: CanvasNode<GenerateImageNodeData>;
  selected: boolean;
  highlighted: boolean;
  userCredits?: number;
}

const MODE_OPTIONS: Array<{ value: NonNullable<GenerateImageNodeData['mode']>; label: string }> = [
  { value: 'text2img', label: '文生图' },
  { value: 'img2img', label: '图生图' },
  { value: 'inpaint', label: '局部重绘' },
];

const ASPECT_OPTIONS = ['auto', '1:1', '16:9', '9:16', '4:3', '3:4'];
const QUALITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: '标准' },
  { value: 'medium', label: '高清' },
  { value: 'high', label: '超清' },
];

export function GenerateImageNode({ node, selected, highlighted, userCredits }: GenerateImageNodeProps) {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [promptLibOpen, setPromptLibOpen] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [maskOpen, setMaskOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const mentionEditorRef = useRef<PromptMentionEditorHandle | null>(null);
  const paramsRef = useRef<HTMLDivElement | null>(null);

  // 只订阅上游签名：上游内容不变时本节点不重渲染，避免 memo 被全量 nodes 订阅击穿
  const upstreamSig = useBoard((s) => buildUpstreamSignature(node.id, s.nodes, s.edges));
  const referencePicker = useBoard((s) => s.referencePicker);
  const readOnly = useBoard((s) => s.readOnly);
  const { nodes, edges } = useMemo(() => getBoardState(), [upstreamSig]);
  const pickingActive = referencePicker?.sourceNodeId === node.id;

  const data = node.data;
  const selectedModelConfig = useMemo(
    () => models.find((model) => model.id === data.modelId),
    [models, data.modelId]
  );
  // 上游聚合（一次计算复用）：模式同步看「有无图」，配额校验看「有几张」
  const upstreamAggregate = useMemo(
    () => aggregateUpstream(node, nodes, edges),
    [node, nodes, edges]
  );
  // 连线驱动的模式同步：上游有无图片输入翻转时在文生图/图生图间自动切换
  const hasImageUpstream = upstreamAggregate.referenceImages.length > 0;
  useUpstreamModeSync({
    nodeId: node.id,
    hasImageUpstream,
    textMode: 'text2img',
    imgMode: 'img2img',
    imageDependentModes: ['img2img', 'inpaint'],
    imgModeAvailable: !selectedModelConfig || selectedModelConfig.enableImg2img !== false,
    textModeAvailable: !selectedModelConfig || selectedModelConfig.enableText2img !== false,
  });
  // 无图上游强制文生图：图生图/局部重绘都依赖参考图输入，无图可选是无效状态
  // （提交必失败）；纠正存量/手动锁定的无效模式（模式选择器同步禁用）
  const textModeAvailable = !selectedModelConfig || selectedModelConfig.enableText2img !== false;
  useEffect(() => {
    if (readOnly) return;
    const current = data.mode || 'text2img';
    if (hasImageUpstream || current === 'text2img' || !textModeAvailable) return;
    boardActions.updateNodeDataSilent(node.id, { mode: 'text2img' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImageUpstream, data.mode, textModeAvailable, node.id, readOnly]);
  // @引用预览：聚合提示词含 @图N 标记时，参考图按标记实际引用数计
  // （与 runImageGeneration 提交口径一致，避免"@只引 1 张但按全量配额误置灰"）
  const upstreamAssetGroups = useMemo(
    () => collectUpstreamImageAssetGroups(node.id, nodes, edges),
    [node.id, nodes, edges]
  );
  const knownRefNos = useMemo(
    () =>
      new Set(
        nodes
          .filter((n) => n.type === 'image' || n.type === 'image_group')
          .map((n) => (n.data as Record<string, unknown>).refNo)
          .filter((v): v is number => typeof v === 'number')
      ),
    [nodes]
  );
  const atPreview = useMemo(
    () => resolveAtReferences(upstreamAggregate.prompt, upstreamAssetGroups, knownRefNos),
    [upstreamAggregate, upstreamAssetGroups, knownRefNos]
  );
  const effectiveReferenceCount = atPreview
    ? atPreview.referenceImages.length
    : upstreamAggregate.referenceImages.length;
  // 参考图数量配额：超出当前模式能力上限时置灰生成入口
  const quotaError = useMemo(
    () =>
      validateImageReferenceQuota(
        data.mode ?? 'text2img',
        effectiveReferenceCount,
        selectedModelConfig?.modeCapabilities
      ),
    [data.mode, effectiveReferenceCount, selectedModelConfig]
  );
  const availableQualityOptions = useMemo(
    () =>
      QUALITY_OPTIONS.filter((option) => {
        if (!selectedModelConfig) return true;
        if (option.value === 'low') return selectedModelConfig.enableStandardSize !== false;
        if (option.value === 'medium') return selectedModelConfig.enableHdSize !== false;
        if (option.value === 'high') return selectedModelConfig.enableUhdSize !== false;
        return true;
      }),
    [selectedModelConfig]
  );
  const selectedQuality = availableQualityOptions.some((option) => option.value === (data.quality || 'low'))
    ? (data.quality || 'low')
    : (availableQualityOptions[0]?.value || 'low');
  const qualityLabel = QUALITY_OPTIONS.find((q) => q.value === selectedQuality)?.label ?? '标准';

  useEffect(() => {
    let cancelled = false;
    getCachedImageModels().then((list) => {
      if (cancelled) return;
      setModels(list);
      if (readOnly) return;
      if (list.length > 0 && (!node.data.modelId || !list.some((m) => m.id === node.data.modelId))) {
        // 未选模型，或参数记忆/历史数据里的模型已下线或被隐藏：
        // 回退到首个可用模型（派生校正，静默写入不触发自动保存）
        boardActions.updateNodeDataSilent(node.id, { modelId: list[0].id });
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (data.quality !== selectedQuality) {
      // quality 按模型能力收敛同样属于派生校正，静默写入
      boardActions.updateNodeDataSilent(node.id, { quality: selectedQuality });
    }
  }, [data.quality, node.id, selectedQuality, readOnly]);

  const onChange = (patch: Partial<GenerateImageNodeData>) => {
    boardActions.updateNodeData(node.id, patch as Record<string, unknown>);
    // 记住用户的参数偏好（模型 / 比例 / 清晰度 / 数量），下次新建生图节点自动套用
    writeNodeParamMemory('generate_image', patch);
  };

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // 上游链路执行中：等待完成后再聚合（防止聚合到流式中途等不完整输出），聚合前重读最新图状态
      await waitForUpstreamIdle(node.id);
      const fresh = getBoardState();
      const upstream = aggregateUpstream(node, fresh.nodes, fresh.edges);
      if (!upstream.prompt) {
        toast.error('请先连接上游文本节点或在本节点填写提示词');
        return;
      }
      const promptLengthError = getPromptLengthError(upstream.prompt);
      if (promptLengthError) {
        toast.error(promptLengthError);
        return;
      }
      await runImageGeneration({
        generateNode: node,
        prompt: upstream.prompt,
        referenceImages: upstream.referenceImages,
        maskImage: data.mode === 'inpaint' ? (data.maskImage || undefined) : undefined,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '生成失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRunChain = async () => {
    try {
      await executeChain(node.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '链路执行失败');
    }
  };

  const generating = !!data.generating || loading;
  const mode = data.mode || 'text2img';

  // ---------------- 下方面板：上游输入 + 提示词编辑 ----------------

  // ---------------- 下方面板：上游输入 + 提示词编辑 ----------------
  const upstreamPanel = (() => {
    const upstreamIds = edges
      .filter((e) => e.targetNodeId === node.id)
      .map((e) => e.sourceNodeId);
    const upstreamNodes = nodes.filter((n) => upstreamIds.includes(n.id));
    if (upstreamNodes.length === 0) return null;
    const inputOrder = (data as Record<string, unknown>).inputOrder as string[] | undefined;
    const ordered = inputOrder && inputOrder.length > 0
      ? [...upstreamNodes].sort((a, b) => {
          const oa = inputOrder.indexOf(a.id);
          const ob = inputOrder.indexOf(b.id);
          return (oa === -1 ? 9999 : oa) - (ob === -1 ? 9999 : ob);
        })
      : upstreamNodes;
    const moveItem = (id: string, dir: -1 | 1) => {
      const currentOrder = (data as Record<string, unknown>).inputOrder as string[] | undefined
        || upstreamIds;
      const idx = currentOrder.indexOf(id);
      if (idx === -1) return;
      const newOrder = [...currentOrder];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= newOrder.length) return;
      [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
      onChange({ inputOrder: newOrder } as Record<string, unknown> as Partial<GenerateImageNodeData>);
    };
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
        <div className="mb-1 text-[10px] font-medium text-slate-400">
          上游输入 ({upstreamNodes.length})，按顺序聚合为提示词与参考图；@图N 按图号引用上游图片（见图上角标），不受连线顺序影响
        </div>
        {ordered.map((n, i) => (
          <div key={n.id} className="flex items-center gap-1.5 py-0.5">
            <span className="text-[10px] text-slate-400">{i + 1}.</span>
            <span className="flex-1 truncate text-slate-600">
              {n.type === 'text'
                 ? htmlToPlainText((n.data as TextNodeData).content || '').slice(0, 30) || '(空文本)'
                : n.type === 'image'
                  ? '图片'
                  : n.type === 'image_group'
                    ? '图片组'
                    : n.type === 'video'
                      ? '视频'
                      : n.type === 'llm'
                        ? 'LLM'
                        : n.type}
            </span>
            <button
              type="button"
              onClick={() => moveItem(n.id, -1)}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
              disabled={i === 0}
            >
              <ArrowUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(n.id, 1)}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30"
              disabled={i === ordered.length - 1}
            >
              <ArrowDown className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  })();

  const panel = (
    <div className="space-y-2">
      {upstreamPanel}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[10px] font-medium text-slate-400">补充提示词（与上游文本合并）</label>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() =>
                pickingActive
                  ? boardActions.cancelReferencePick()
                  : boardActions.startReferencePick(node.id)
              }
              className={`flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] transition ${
                pickingActive ? 'bg-blue-100 text-blue-700' : 'text-blue-500 hover:bg-blue-50'
              }`}
              title="进入点选模式，在画布上点击节点以连接为参考图"
            >
              <MousePointerClick className="h-3 w-3" />
              {pickingActive ? '退出点选' : '点选'}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMentionOpen((v) => !v)}
                disabled={upstreamAssetGroups.length === 0}
                className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                title="插入 @图N 引用：提示词中显式指定某张上游图片作参考图"
              >
                <AtSign className="h-3 w-3" />
                引用
              </button>
              {mentionOpen && (() => {
                // 仅列出有图上游；@图N 按节点稳定图号（角标 #N）插入，与连线顺序无关
                if (upstreamAssetGroups.length === 0) {
                  return (
                    <div className="absolute right-0 top-full z-30 mt-1 w-[170px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] text-slate-400 shadow-lg">
                      上游还没有可引用的图片，先连接图片节点或图片组
                    </div>
                  );
                }
                return (
                  <div className="absolute right-0 top-full z-30 mt-1 max-h-44 min-w-[170px] overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                    {upstreamAssetGroups.map((group) => {
                      const refNo = group.refNo;
                      if (typeof refNo !== 'number') return null;
                      const src = resolveBrowserMediaUrl(group.urls[0]) || group.urls[0];
                      return (
                        <button
                          key={group.nodeId}
                          type="button"
                          onClick={() => {
                            mentionEditorRef.current?.insertMention(refNo);
                            setMentionOpen(false);
                          }}
                          className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50"
                          title={
                            group.urls.length > 1
                              ? `插入 @图${refNo}（该上游为图片组，共 ${group.urls.length} 张）`
                              : `插入 @图${refNo}`
                          }
                        >
                          <img src={src} alt="" className="h-6 w-6 rounded border border-slate-100 object-cover" />
                          <span className="truncate">
                            @图{refNo}
                            {group.type === 'image_group' && (
                              <span className="ml-1 text-[10px] text-slate-400">（组 {group.urls.length} 张）</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => setPromptLibOpen(true)}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-blue-500 hover:bg-blue-50"
              title="从提示词库选择"
            >
              <BookOpen className="h-3 w-3" />
              提示词库
            </button>
            <button
              type="button"
              onClick={() => setPromptExpanded(true)}
              className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-100"
              title="放大编辑"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className="relative">
          <PromptMentionEditor
            ref={mentionEditorRef}
            value={data.prompt || ''}
            onChange={(v) => onChange({ prompt: v })}
            placeholder={'补充提示词，与上游文本合并；可用 @图N（见图上角标）引用某张上游图片作参考图'}
            className="h-16 resize-none"
            options={upstreamAssetGroups
              .filter((g): g is typeof g & { refNo: number } => typeof g.refNo === 'number')
              .map((g) => ({
                refNo: g.refNo,
                thumbnail: resolveBrowserMediaUrl(g.urls[0]) || g.urls[0],
                isGroup: g.type === 'image_group',
                count: g.urls.length,
              }))}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={handleRunChain}
        disabled={generating || !!quotaError}
        title={quotaError ?? undefined}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-100 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Play className="h-3.5 w-3.5" />
        <span>运行链路</span>
      </button>
    </div>
  );

  // ---------------- 卡片：模式分段 + 模型 + 参数摘要 + 生成 ----------------
  return (
    <NodeWrapper node={node} selected={selected} highlighted={highlighted} panel={panel} stretchContent>
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 text-[11px]">
        {/* 模式分段：图生图/局部重绘依赖参考图输入，无图上游时禁用（先连图再切换） */}
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {MODE_OPTIONS.map((opt) => {
            const blocked = !hasImageUpstream && opt.value !== 'text2img';
            return (
              <button
                key={opt.value}
                type="button"
                disabled={blocked}
                onClick={() => onChange({ mode: opt.value })}
                title={blocked ? '请先连接参考图（图片节点或图片组）' : undefined}
                className={`flex-1 rounded-md py-1 text-[10px] font-medium transition ${
                  mode === opt.value
                    ? 'bg-white text-slate-800 shadow-sm'
                    : blocked
                      ? 'cursor-not-allowed text-slate-300'
                      : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* 模型 + 参数摘要（弹层） */}
        <div className="flex gap-1.5">
          <select
            value={data.modelId || ''}
            onChange={(e) => onChange({ modelId: e.target.value })}
            className="min-w-0 flex-1 rounded-md border border-light-border bg-white px-2 py-1.5 text-xs"
            title="图片模型"
          >
            <option value="">选择模型</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <div className="relative" ref={paramsRef}>
            <button
              type="button"
              onClick={() => setParamsOpen((v) => !v)}
              className={`flex h-full items-center gap-1 rounded-md border px-2 text-[10px] font-medium transition ${
                paramsOpen ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-light-border bg-white text-slate-600 hover:bg-slate-50'
              }`}
              title="比例 · 质量 · 数量"
            >
              {data.aspectRatio || 'auto'} · {qualityLabel} · {data.count ?? 1}张
              <ChevronDown className="h-3 w-3" />
            </button>
            <NodeParamsPopover open={paramsOpen} onClose={() => setParamsOpen(false)} anchor={paramsRef} width={192}>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">比例</label>
                <select
                  value={data.aspectRatio || 'auto'}
                  onChange={(e) => onChange({ aspectRatio: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {ASPECT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">质量</label>
                <select
                  value={selectedQuality}
                  onChange={(e) => onChange({ quality: e.target.value })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                >
                  {availableQualityOptions.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-slate-400">数量（1-4）</label>
                <input
                  type="number"
                  min={1}
                  max={4}
                  value={data.count ?? 1}
                  onChange={(e) => onChange({ count: Math.max(1, Math.min(4, Number(e.target.value) || 1)) })}
                  className="w-full rounded-md border border-light-border bg-white px-2 py-1 text-xs"
                />
              </div>
            </NodeParamsPopover>
          </div>
        </div>

        {/* 蒙版（inpaint） */}
        {mode === 'inpaint' && (
          <div className="flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5">
            <span className="flex items-center gap-1 text-[10px] font-medium text-orange-700">
              <Paintbrush className="h-3 w-3" />
              {data.maskImage ? '蒙版已绘制' : '需绘制蒙版'}
            </span>
            <div className="flex items-center gap-1.5">
              {data.maskImage && (
                <button
                  type="button"
                  onClick={() => onChange({ maskImage: null, _maskStorageKey: null })}
                  className="text-[10px] text-red-500 hover:underline"
                >
                  清除
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  const upstream = aggregateUpstream(node, nodes, edges);
                  if (!upstream.referenceImages.length) {
                    toast.error('请先连接一张参考图');
                    return;
                  }
                  setMaskOpen(true);
                }}
                className="rounded-md bg-orange-500 px-2 py-1 text-[10px] font-medium text-white transition hover:bg-orange-600"
              >
                {data.maskImage ? '重新绘制' : '绘制蒙版'}
              </button>
            </div>
          </div>
        )}

        <ImageCreditsPreview
          model={selectedModelConfig}
          mode={mode as GenerationMode}
          quality={selectedQuality}
          count={data.count ?? 1}
          userCredits={userCredits}
        />

        {/* 参考图数量配额不满足：置灰生成入口并说明差距 */}
        {quotaError && (
          <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] leading-relaxed text-red-600">
            {quotaError}
          </p>
        )}

        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading || !!quotaError}
          title={quotaError ?? undefined}
          className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg bg-blue-500 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {generating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>重新生成</span>
            </>
          ) : (
            <>
              <Wand2 className="h-3.5 w-3.5" />
              <span>生成图片</span>
            </>
          )}
        </button>
      </div>

      <PromptLibraryModal
        open={promptLibOpen}
        onClose={() => setPromptLibOpen(false)}
        onSelect={(prompt) => {
          const existing = data.prompt || '';
          onChange({ prompt: existing ? `${existing}\n${prompt}` : prompt });
        }}
      />
      {maskOpen && (() => {
        const upstream = aggregateUpstream(node, nodes, edges);
        const refImg = upstream.referenceImages[0];
        if (!refImg) return null;
        return (
          <MaskDialog
            imageUrl={refImg}
            initialMask={data.maskImage}
            onSave={({ maskDataUrl }) => {
              // 蒙版转存 IndexedDB，节点只存 blob 引用，避免 base64 撑大快照
              void (async () => {
                try {
                  const blob = await (await fetch(maskDataUrl)).blob();
                  const { blobUrl, storageKey } = await uploadMaskToIndexedDB(blob, getBoardState().projectId ?? undefined);
                  onChange({ maskImage: blobUrl, _maskStorageKey: storageKey });
                } catch {
                  // IndexedDB 不可用时降级为旧行为（data URL 直存），提交生成仍可用
                  onChange({ maskImage: maskDataUrl });
                }
              })();
              setMaskOpen(false);
            }}
            onClose={() => setMaskOpen(false)}
          />
        );
      })()}
      {promptExpanded && (
        <TextEditorDialog
          title="补充提示词"
          initialValue={data.prompt || ''}
          placeholder="补充提示词，与上游文本合并；可用 @图N（见图上角标）引用某张上游图片作参考图"
          onCommit={(html) => {
            onChange({ prompt: htmlToPlainText(html) });
            setPromptExpanded(false);
          }}
          onClose={() => setPromptExpanded(false)}
        />
      )}
    </NodeWrapper>
  );
}
